import { useQuery } from '@apollo/client';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useDescope, useSession, useUser } from '@descope/react-sdk';
import { EXTERNAL_USER_QUERY } from '@gql/queries/externalUser';
import type { ExternalUserQuery } from '@gql/types/graphql';

export type ExternalUser = ExternalUserQuery['externalUser'];

export interface AuthClient {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: ExternalUser;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthClient | undefined>(undefined);

const isNotFoundError = (error: Error | null | undefined) =>
  Boolean(error && 'statusCode' in error && error.statusCode === 404);

export function AuthProvider({ children }: React.PropsWithChildren) {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { isUserLoading, user } = useUser();
  const { logout } = useDescope();
  const providerId = isAuthenticated && !isUserLoading ? user?.userId : undefined;
  const {
    data,
    error,
    loading: isExternalUserLoading,
  } = useQuery(EXTERNAL_USER_QUERY, {
    skip: !providerId,
    variables: { providerId: providerId ?? '' },
    fetchPolicy: 'network-only',
  });
  const externalUser = data?.externalUser;
  const hasInactiveExternalUser = externalUser?.isActive === false;
  const hasMissingExternalUser = isNotFoundError(error?.networkError);
  const shouldLogout = hasInactiveExternalUser || hasMissingExternalUser;

  useEffect(() => {
    if (isAuthenticated && shouldLogout) {
      void logout();
    }
  }, [isAuthenticated, logout, shouldLogout]);

  const auth = useMemo<AuthClient>(
    () => ({
      isAuthenticated: isAuthenticated && !shouldLogout,
      isLoading: isSessionLoading || (isAuthenticated && (isUserLoading || isExternalUserLoading)),
      user: !shouldLogout && externalUser?.isActive ? externalUser : undefined,
      logout: async () => {
        await logout();
      },
    }),
    [externalUser, isAuthenticated, isExternalUserLoading, isSessionLoading, isUserLoading, logout, shouldLogout],
  );

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const auth = useContext(AuthContext);

  if (!auth) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return auth;
}
